import React, { useState, useRef } from "react";
import { Building, Phone, Mail } from "lucide-react";

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  const validateForm = () => {
    const errors = {};

    if (formData.firstName.length < 2) {
      errors.firstName = "First name must be at least 2 characters";
    }

    if (formData.lastName.length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
    }

    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (formData.phone.length < 10) {
      errors.phone = "Please enter a valid phone number";
    }

    if (formData.message.length < 10) {
      errors.message = "Message must be at least 10 characters";
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);

      try {
        const ACCESS_KEY = "45cb9274-b96e-47df-8e9f-6468044ef4e7";

        const response = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            access_key: ACCESS_KEY,
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
          }),
        });

        const result = await response.json();

        if (result.success) {
          setIsSuccess(true);
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            message: "",
          });

          setTimeout(() => setIsSuccess(false), 5000);
        } else {
          console.error("Form submission error:", result);
        }
      } catch (error) {
        console.error("Form submission error:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div style={{ display: "flex", padding: "20px", justifyContent: "center", backgroundColor: "#f4f4f4" }}>
      <div style={{ width: "100%", maxWidth: "1200px", display: "flex", flexDirection: "row" }}>
        {/* Sidebar */}
        <div style={{ flex: 1, padding: "20px", backgroundColor: "#fff", marginRight: "20px", borderRadius: "8px" }}>
          <div style={{ position: "relative", padding: "20px", borderRadius: "8px", backgroundColor: "#f9f9f9" }}>
            <h2 style={{ fontSize: "24px", fontWeight: "bold" }}>Get in touch</h2>
            <p style={{ marginBottom: "20px", color: "#777" }}>
              Have questions or need assistance? Feel free to reach out! Whether it's inquiries, feedback, or collaborations, we're here to help.
            </p>

            <div>
              {/* Contact Info */}
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                  <Building size={24} style={{ marginRight: "10px" }} />
                  <div>
                    <p style={{ fontSize: "16px", fontWeight: "bold" }}>Address</p>
                    <p style={{ color: "#555" }}>707 A, NDM 1, Netaji Subash Place, New Delhi</p>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                  <Phone size={24} style={{ marginRight: "10px" }} />
                  <div>
                    <p style={{ fontSize: "16px", fontWeight: "bold" }}>Phone</p>
                    <p style={{ color: "#555" }}>+91 95550 40001</p>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                  <Mail size={24} style={{ marginRight: "10px" }} />
                  <div>
                    <p style={{ fontSize: "16px", fontWeight: "bold" }}>Email</p>
                    <p style={{ color: "#555" }}>sales@metalzulu.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "30px", textAlign: "center", color: "#777", fontSize: "14px" }}>
              <p>© 2025 IMAX. All rights reserved.</p>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div style={{ flex: 2, padding: "20px", backgroundColor: "#fff", borderRadius: "8px" }}>
          <h2 style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "10px", marginTop: "10px" }}>Send us a message</h2>
          <p style={{ color: "#777", marginBottom: "20px" }}>
            Fill out the form below and we'll respond as soon as possible.
          </p>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", marginBottom: "15px" }}>
              <div style={{ flex: 1, paddingRight: "10px" }}>
                <label style={{ fontSize: "14px", fontWeight: "bold" }}>First name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  style={{ width: "100%", padding: "10px", border: "1px solid #ccc", borderRadius: "4px" }}
                />
                {formErrors.firstName && (
                  <p style={{ color: "red", fontSize: "12px" }}>{formErrors.firstName}</p>
                )}
              </div>
              <div style={{ flex: 1, paddingLeft: "10px" }}>
                <label style={{ fontSize: "14px", fontWeight: "bold" }}>Last name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  style={{ width: "100%", padding: "10px", border: "1px solid #ccc", borderRadius: "4px" }}
                />
                {formErrors.lastName && (
                  <p style={{ color: "red", fontSize: "12px" }}>{formErrors.lastName}</p>
                )}
              </div>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontSize: "14px", fontWeight: "bold" }}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                style={{ width: "100%", padding: "10px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
              {formErrors.email && <p style={{ color: "red", fontSize: "12px" }}>{formErrors.email}</p>}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontSize: "14px", fontWeight: "bold" }}>Phone number</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone number"
                style={{ width: "100%", padding: "10px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
              {formErrors.phone && <p style={{ color: "red", fontSize: "12px" }}>{formErrors.phone}</p>}
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontSize: "14px", fontWeight: "bold" }}>Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Write your message here..."
                style={{
                  width: "100%",
                  height: "150px",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
              {formErrors.message && <p style={{ color: "red", fontSize: "12px" }}>{formErrors.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {isSubmitting ? "Sending..." : isSuccess ? "Message sent!" : "Send message"}
            </button>

            {isSuccess && (
              <div style={{ marginTop: "20px", color: "green", fontSize: "14px" }}>
                Thank you! Your message has been sent successfully.
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
